import { useContext } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { SkillsClientContext } from 'components/shared/Skills/SkillsClientProvider';

export const SORT_OPTIONS = {
  ASC: 'asc',
  DESC: 'desc',
  CATEGORY_ASC: 'category_asc',
  CATEGORY_DESC: 'category_desc',
};

export function useGetReadinessSkills({
  limit,
  offset,
  searchTerm,
  sort,
  excludedIds = [],
  onlyTags = false,
  skip = false,
}) {
  const { client } = useContext(SkillsClientContext);

  const variables = {
    limit,
    offset,
    searchTerm,
    sort,
    onlyTags,
    excludedIds,
    skip,
  };

  return useQuery(
    [
      'get-readiness-skills',
      limit,
      offset,
      searchTerm,
      sort,
      onlyTags,
      excludedIds,
    ],
    async () => {
      return client.request(
        gql`
          query readinessSkills(
            $limit: Int!
            $offset: Int!
            $searchTerm: String
            $sort: ReadinessSkillsSortOptions
            $onlyTags: Boolean
            $excludedIds: [Int]
          ) {
            readinessSkills(
              limit: $limit
              offset: $offset
              searchTerm: $searchTerm
              sort: $sort
              onlyTags: $onlyTags
              excludedIds: $excludedIds
            ) {
              readinessSkills {
                id
                name
                description
                readinessSkillCategory {
                  name
                  id
                }
              }
              totalCount
            }
          }
        `,
        variables,
      );
    },
    { enabled: !skip },
  );
}

export function useGetReadinessSkillsCategories({ limit, offset }) {
  const { client } = useContext(SkillsClientContext);

  const variables = {
    limit,
    offset,
  };

  return useQuery(
    ['get-readiness-skills-categories', limit, offset],
    async () => {
      return client.request(
        gql`
          query readinessSkillsCategories($limit: Int!, $offset: Int!) {
            readinessSkillsCategories(limit: $limit, offset: $offset) {
              readinessSkillCategories {
                id
                name
              }
              totalCount
            }
          }
        `,
        variables,
      );
    },
  );
}

export function useCreateReadinessSkillCategory() {
  const { client } = useContext(SkillsClientContext);
  return useMutation({
    mutationFn: variables =>
      client.request(
        gql`
          mutation createReadinessSkillCategory($name: String!) {
            createReadinessSkillCategory(name: $name) {
              id
              name
            }
          }
        `,
        variables,
      ),
  });
}

export function useCreateReadinessSkill(
  offset = 0,
  limit = 10,
  searchTerm = '',
) {
  const { client } = useContext(SkillsClientContext);
  return useMutation({
    mutationFn: async variables =>
      client.request(
        gql`
          mutation createReadinessSkill(
            $name: String!
            $description: String
            $readinessSkillCategoryId: Int
          ) {
            createReadinessSkill(
              name: $name
              description: $description
              readinessSkillCategoryId: $readinessSkillCategoryId
            ) {
              id
              name
            }
          }
        `,
        variables,
      ),
  });
}

export function useUpdateReadinessSkillCategory() {
  const { client } = useContext(SkillsClientContext);
  return useMutation({
    mutationFn: variables =>
      client.request(
        gql`
          mutation updateReadinessSkillCategory($id: Int!, $name: String) {
            updateReadinessSkillCategory(id: $id, name: $name) {
              id
              name
            }
          }
        `,
        variables,
      ),
  });
}

export function useUpdateReadinessSkill() {
  const { client } = useContext(SkillsClientContext);
  return useMutation({
    mutationFn: variables =>
      client.request(
        gql`
          mutation updateReadinessSkill(
            $id: Int!
            $name: String
            $description: String
            $readinessSkillCategoryId: Int
          ) {
            updateReadinessSkill(
              id: $id
              name: $name
              description: $description
              readinessSkillCategoryId: $readinessSkillCategoryId
            ) {
              id
              name
              description
              readinessSkillCategory {
                id
                name
              }
            }
          }
        `,
        variables,
      ),
  });
}

export function useDeleteReadinessSkillCategory() {
  const { client } = useContext(SkillsClientContext);
  return useMutation({
    mutationFn: variables =>
      client.request(
        gql`
          mutation deleteReadinessSkillCategory($id: Int!) {
            deleteReadinessSkillCategory(id: $id)
          }
        `,
        variables,
      ),
  });
}

export function useDeleteReadinessSkill() {
  const { client } = useContext(SkillsClientContext);
  return useMutation({
    mutationFn: variables =>
      client.request(
        gql`
          mutation deleteReadinessSkill($id: Int!) {
            deleteReadinessSkill(id: $id)
          }
        `,
        variables,
      ),
  });
}
